import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useNewsletterItems } from '@/hooks/useNewsletterItems'
import { RECORDINGS_BUCKET } from '@/lib/upload'
import type { Headline, Story, Voice } from '@/types/database'
import type { LibraryStory, StoryProgress, StoryProgressPatch } from '@/types/library'

/**
 * The Library's data (Brian's library.html v2.1, rec-pick.js).
 *
 * Videos live in the phone's Photos, so the Library tracks STORIES and where
 * each one is: Writing → To record → Recorded → Posted (a light record that
 * clears itself 90 days after "I posted it"). The status lives server side in
 * story_progress, so the desktop and the phone agree (Send to phone).
 *
 * For other areas:
 *   useSendToPhone()   → mutate({ storyId, on })
 *   useToRecordCount() → number                 the Record ＋ tab's count
 *   useLibraryStories() → { stories, writing, isLoading }
 */

const DAY = 864e5
export const POSTED_KEEP_DAYS = 90
export const STALE_DAYS = 7
/** How long a build may run before we stop showing it as "Writing". */
const WRITING_WINDOW_MS = 10 * 60_000

const t = (iso: string | null | undefined) => (iso ? Date.parse(iso) : null)

interface Raw {
  stories: (Story & { headlines?: { source_published_at: string | null } | null })[]
  progress: StoryProgress[]
  versions: { story_id: string; voice: Voice; created_at: string }[]
  recordings: { story_id: string; size_bytes: number | null; storage_path: string | null; on_device?: boolean | null; posted_at?: string | null; created_at: string }[]
  writing: { headline_id: string; decided_at: string; headlines: { headline: string; topic: string | null; image_url: string | null; image_credit: string | null } | null }[]
}

export function useLibraryRaw() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['library', clientId, userId],
    enabled: !!clientId,
    // While a build is being written, look again every few seconds.
    refetchInterval: (q) => {
      const d = q.state.data as Raw | undefined
      return d && (d.writing.length || d.stories.some((s) => s.status === 'developing')) ? 4000 : false
    },
    queryFn: async (): Promise<Raw> => {
      const since = new Date(Date.now() - WRITING_WINDOW_MS).toISOString()
      const [s, p, v, r, w] = await Promise.all([
        supabase.from('stories').select('*').eq('client_id', clientId!).order('created_at', { ascending: false }).limit(300),
        supabase.from('story_progress').select('*').eq('client_id', clientId!),
        supabase.from('story_versions').select('story_id, voice, created_at').eq('client_id', clientId!).order('created_at', { ascending: true }),
        supabase.from('recordings').select('*').eq('client_id', clientId!),
        // A build records the Build decision first, then writes for up to a
        // minute or two. A Build with no story yet is a story being written.
        userId
          ? supabase.from('headline_decisions')
            .select('headline_id, decided_at, headlines(headline, topic, image_url, image_credit)')
            .eq('client_id', clientId!).eq('user_id', userId).eq('decision', 'right').gte('decided_at', since)
          : Promise.resolve({ data: [], error: null }),
      ])
      if (s.error) throw s.error
      // The progress table is new; read on without it rather than blank the Library.
      return {
        stories: (s.data ?? []) as Raw['stories'],
        progress: p.error ? [] : (p.data ?? []) as StoryProgress[],
        versions: (v.data ?? []) as Raw['versions'],
        recordings: (r.data ?? []) as Raw['recordings'],
        writing: (w.error ? [] : (w.data ?? [])) as unknown as Raw['writing'],
      }
    },
  })
}

/** Every built story (and any still being written), newest first. */
export function useLibraryStories() {
  const raw = useLibraryRaw()
  const nl = useNewsletterItems()
  const stories = useMemo<LibraryStory[]>(() => {
    const d = raw.data
    if (!d) return []
    const now = Date.now()
    const prog = new Map(d.progress.map((p) => [p.story_id, p]))
    const pos = new Map<string, Voice[]>()
    d.versions.forEach((v) => { const l = pos.get(v.story_id) ?? []; if (!l.includes(v.voice)) l.push(v.voice); pos.set(v.story_id, l) })
    // Record's contract (hooks/useRecordings.ts, migration 20240039): any
    // recordings row means Recorded, a row with posted_at means Posted. Only an
    // old upload (a storage_path, not on_device) is a take GoGrov still holds.
    const takes = new Map<string, { n: number; bytes: number }>()
    const recAt = new Map<string, number>(), postAt = new Map<string, number>()
    d.recordings.forEach((r) => {
      const c = Date.parse(r.created_at)
      if (!recAt.has(r.story_id) || c < recAt.get(r.story_id)!) recAt.set(r.story_id, c)
      if (r.posted_at) postAt.set(r.story_id, Math.max(postAt.get(r.story_id) ?? 0, Date.parse(r.posted_at)))
      if (r.storage_path && !r.on_device) { const x = takes.get(r.story_id) ?? { n: 0, bytes: 0 }; x.n++; x.bytes += r.size_bytes ?? 0; takes.set(r.story_id, x) }
    })

    const built: LibraryStory[] = d.stories.filter((s) => s.status !== 'skipped').map((s) => {
      const p = prog.get(s.id)
      const tk = takes.get(s.id)
      const at = Date.parse(s.created_at)
      const recordedAt = t(p?.recorded_at) ?? recAt.get(s.id) ?? null
      const postedAt = t(p?.posted_at) ?? postAt.get(s.id) ?? null
      // build-story's background mode inserts the row at once as 'developing'
      // (with build_stage) and fills it in when Emma is done: that is Writing.
      // A failed build deletes the row, so it simply drops out.
      const developing = s.status === 'developing'
      return {
        id: s.id,
        stage: developing ? 'writing' : postedAt ? 'posted' : recordedAt ? 'recorded' : 'todo',
        headline: s.headline,
        topic: s.category,
        imageUrl: s.image_url ?? null,
        imageCredit: s.image_credit ?? null,
        at,
        stale: now - at > STALE_DAYS * DAY,
        record: s.provenance === 'record',
        positions: pos.get(s.id) ?? [(s.voice ?? 'newcomer') as Voice],
        recordedAt, savedAt: t(p?.saved_at), postedAt,
        toPhoneAt: recordedAt || postedAt ? null : t(p?.to_phone_at),
        inNewsletter: nl.has({ storyId: s.id, headlineId: s.headline_id }),
        takes: tk?.n ?? 0, takeBytes: tk?.bytes ?? 0,
        story: s,
        hidden: !!p?.hidden_at,
      } as LibraryStory & { hidden: boolean }
    }).filter((x) => !(x as LibraryStory & { hidden: boolean }).hidden &&
      !(x.postedAt && x.postedAt < now - POSTED_KEEP_DAYS * DAY))

    const builtFrom = new Set(d.stories.map((s) => s.headline_id).filter(Boolean))
    const writing: LibraryStory[] = d.writing.filter((w) => !builtFrom.has(w.headline_id) && w.headlines).map((w) => ({
      id: w.headline_id, stage: 'writing', headline: w.headlines!.headline, topic: w.headlines!.topic ?? 'civic-government',
      imageUrl: w.headlines!.image_url, imageCredit: w.headlines!.image_credit, at: Date.parse(w.decided_at), stale: false,
      record: false, positions: [], recordedAt: null, savedAt: null, postedAt: null, toPhoneAt: null,
      inNewsletter: nl.has({ headlineId: w.headline_id }), takes: 0, takeBytes: 0, story: null,
    }))
    return [...writing, ...built].sort((a, b) => b.at - a.at)
  }, [raw.data, nl.items]) // eslint-disable-line react-hooks/exhaustive-deps

  return { ...raw, stories }
}

/** Stories waiting in Record ＋ (built, not recorded, not older than a week). */
export function useToRecordCount(): number {
  const { stories } = useLibraryStories()
  return stories.filter((s) => s.stage === 'todo' && (!s.stale || s.toPhoneAt)).length
}

/** One story's progress row (null until anything has happened to it). */
export function useStoryProgress(storyId: string | undefined | null) {
  return useQuery({
    queryKey: ['story-progress', storyId],
    enabled: !!storyId,
    queryFn: async () => {
      const { data, error } = await supabase.from('story_progress').select('*').eq('story_id', storyId!).maybeSingle()
      if (error) throw error
      return (data ?? null) as StoryProgress | null
    },
  })
}

/** Sets fields on a story's progress row, creating it when needed. */
export function useSetStoryProgress() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ storyId, patch }: { storyId: string; patch: StoryProgressPatch }) => {
      const { data, error } = await supabase.from('story_progress')
        .upsert({ story_id: storyId, client_id: clientId, ...patch }, { onConflict: 'story_id' })
        .select('*').single()
      if (error) throw error
      return data as StoryProgress
    },
    onSuccess: (_d, { storyId }) => {
      queryClient.invalidateQueries({ queryKey: ['library', clientId] })
      queryClient.invalidateQueries({ queryKey: ['story-progress', storyId] })
    },
  })
}

const now = () => new Date().toISOString()

/** Desktop "Send to phone": waiting in Record ＋ on every device, script ready. */
export function useSendToPhone() {
  const set = useSetStoryProgress()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  return useMutation({
    mutationFn: ({ storyId, on = true }: { storyId: string; on?: boolean }) =>
      set.mutateAsync({ storyId, patch: { to_phone_at: on ? now() : null, ...(on ? { to_phone_by: userId } : {}) } }),
  })
}

export type StoryOrHeadline =
  | { kind: 'story'; story: Story; headline: Headline | null }
  | { kind: 'headline'; story: null; headline: Headline }
  | { kind: 'missing'; story: null; headline: null }

/**
 * What /app/story/:id shows. The id is a built story's, or a feed card's
 * headline id (Brian, article.html v2.6: a tap on a feed card opens the story;
 * not built yet, it is a preview with Build this story). A headline that was
 * built since resolves to its story.
 */
export function useStoryOrHeadline(id: string | undefined) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['story-or-headline', clientId, id],
    enabled: !!id && !!clientId,
    queryFn: async (): Promise<StoryOrHeadline> => {
      const headlineOf = async (hid: string | null) => {
        if (!hid) return null
        const { data } = await supabase.from('headlines').select('*').eq('id', hid).maybeSingle()
        return (data ?? null) as Headline | null
      }
      const { data: s, error } = await supabase.from('stories').select('*').eq('id', id!).maybeSingle()
      if (error && !/invalid input syntax/i.test(error.message)) throw error
      if (s) return { kind: 'story', story: s as Story, headline: await headlineOf((s as Story).headline_id) }
      const { data: built } = await supabase.from('stories').select('*')
        .eq('client_id', clientId!).eq('headline_id', id!).order('created_at', { ascending: false }).limit(1).maybeSingle()
      if (built) return { kind: 'story', story: built as Story, headline: await headlineOf(id!) }
      const h = await headlineOf(id!)
      if (h) return { kind: 'headline', story: null, headline: h }
      return { kind: 'missing', story: null, headline: null }
    },
  })
}

/**
 * Removes GoGrov's copy of a story's takes (old uploads; the app no longer
 * uploads). Brian, library.html v2.1: once the video is in Photos, GoGrov
 * keeps no backup. File first, then the row, as useDeleteRecording does.
 */
export function useClearTakes() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (storyId: string) => {
      // Uploads only: a device row (on_device, no file) is the story's
      // Recorded / Posted state and must stay.
      const { data, error } = await supabase.from('recordings').select('id, storage_path')
        .eq('story_id', storyId).eq('on_device', false).not('storage_path', 'is', null)
      if (error) throw error
      const rows = (data ?? []) as { id: string; storage_path: string }[]
      if (!rows.length) return 0
      const { error: sErr } = await supabase.storage.from(RECORDINGS_BUCKET).remove(rows.map((r) => r.storage_path))
      if (sErr && !/not found/i.test(sErr.message)) throw sErr
      const { error: dErr } = await supabase.from('recordings').delete().in('id', rows.map((r) => r.id))
      if (dErr) throw dErr
      return rows.length
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library', clientId] })
      queryClient.invalidateQueries({ queryKey: ['recording-library', clientId] })
      queryClient.invalidateQueries({ queryKey: ['storage-used', clientId] })
    },
  })
}
